import { Box } from '@material-ui/core';
import { SelectFilter } from '@agilelab/plugin-wb-platform';
import {
  FeatureRequestStatus,
  FeatureRequestPriority,
  getEntityDisplayName,
  entityToUrn,
} from '@agilelab/plugin-wb-builder-common';
import { Entity, RELATION_HAS_PART } from '@mainstage/catalog-model';
import { useEntity, useRelatedEntities } from '@mainstage/plugin-catalog-react';
import { useMemo } from 'react';

export interface FeatureRequestFilterValues {
  status?: FeatureRequestStatus;
  priority?: FeatureRequestPriority;
  componentUrn?: string;
}

interface FeatureRequestFiltersProps {
  values: FeatureRequestFilterValues;
  onChange: (filters: FeatureRequestFilterValues) => void;
}

const STATUS_OPTIONS = Object.values(FeatureRequestStatus);
const PRIORITY_OPTIONS = Object.values(FeatureRequestPriority);

export const FeatureRequestFilters = ({
  values,
  onChange,
}: FeatureRequestFiltersProps) => {
  const { entity } = useEntity();
  const { entities: components } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  const hasComponents = components && components.length > 0;

  const componentOptions = useMemo(
    () => (components as Entity[] | undefined)?.map(c => entityToUrn(c)) ?? [],
    [components],
  );

  const componentDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        (components as Entity[] | undefined)?.map(c => [
          entityToUrn(c),
          getEntityDisplayName(c),
        ]) ?? [],
      ) as Record<string, string>,
    [components],
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{ gap: 16 }}
      flexWrap="wrap"
      mb={2}
      role="group"
      aria-label="Feature request filters"
    >
      <SelectFilter<FeatureRequestStatus>
        field="Status"
        value={values.status}
        options={STATUS_OPTIONS}
        renderOption={o => o.replace('_', ' ')}
        renderValue={v => v.replace('_', ' ')}
        onChange={val => onChange({ ...values, status: val })}
      />

      <SelectFilter<FeatureRequestPriority>
        field="Priority"
        value={values.priority}
        options={PRIORITY_OPTIONS}
        renderOption={o => o}
        renderValue={v => v}
        onChange={val => onChange({ ...values, priority: val })}
      />

      {hasComponents && (
        <SelectFilter<string>
          field="Component"
          value={values.componentUrn}
          options={componentOptions}
          renderOption={o => componentDisplayNames[o] ?? o}
          renderValue={v => componentDisplayNames[v] ?? v}
          onChange={val => onChange({ ...values, componentUrn: val })}
        />
      )}
    </Box>
  );
};
